import React, { useState } from "react";
import { toast } from "sonner";
import api, { formatApiError } from "@/lib/api";
import { Link } from "react-router-dom";
import { Mail } from "lucide-react";

export default function NewsletterForm() {
  const [email, setEmail] = useState("");
  const [consent, setConsent] = useState(true);
  const [loading, setLoading] = useState(false);
  const [done, setDone] = useState(false);

  async function submit(e) {
    e.preventDefault();
    if (!consent) { toast.error("Bitte stimmen Sie der Datenschutzerklärung zu."); return; }
    setLoading(true);
    try {
      const { data } = await api.post("/newsletter", { email, consent });
      setDone(true);
      toast.success(data.message || "Vielen Dank. Ihre Anmeldung wurde gespeichert.");
      setEmail("");
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail) || "Anmeldung fehlgeschlagen.");
    } finally { setLoading(false); }
  }

  return (
    <div id="newsletter" className="bg-white border border-brand-line p-6 sm:p-8 lg:p-10" data-testid="newsletter-form">
      <div className="grid lg:grid-cols-12 gap-6 lg:gap-10 items-center">
        {/* Icon + Text */}
        <div className="lg:col-span-6 flex items-center gap-5">
          <div className="hidden sm:flex h-14 w-14 rounded-full border-2 border-[hsl(var(--brand-green))] items-center justify-center shrink-0 text-[hsl(var(--brand-green-strong))]">
            <Mail className="h-6 w-6" strokeWidth={1.5} />
          </div>
          <div>
            <h2 className="font-semibold text-2xl sm:text-[26px] mb-1.5">Newsletter abonnieren</h2>
            <p className="text-sm text-neutral-600 leading-relaxed max-w-md">
              Bleiben Sie auf dem Laufenden! Abonnieren Sie unseren Newsletter und erhalten Sie alle wichtigen Neuigkeiten direkt per E-Mail.
            </p>
          </div>
        </div>

        {/* Form */}
        <form onSubmit={submit} className="lg:col-span-6" data-testid="newsletter-form-element">
          {done ? (
            <div className="bg-[hsl(var(--brand-soft))] border border-[hsl(var(--brand-green))] p-5 text-sm text-neutral-700" data-testid="newsletter-success">
              <p className="font-medium text-[hsl(var(--brand-green-strong))] mb-1">Fast geschafft!</p>
              <p>Wir haben Ihnen eine Bestätigungs-E-Mail gesendet. Bitte öffnen Sie die Mail und klicken Sie auf den Bestätigungs-Link, um Ihre Newsletter-Anmeldung abzuschließen.</p>
            </div>
          ) : (
            <>
              <div className="flex flex-col sm:flex-row gap-3">
                <input
                  type="email"
                  required
                  placeholder="E-Mail-Adresse eingeben"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  data-testid="newsletter-email"
                  className="flex-1 bg-white border border-brand-line px-4 py-3.5 text-sm focus:outline-none focus:border-[hsl(var(--brand-green-strong))] focus:ring-1 focus:ring-[hsl(var(--brand-green-strong))]"
                />
                <button
                  type="submit"
                  disabled={loading}
                  data-testid="newsletter-submit"
                  className="bg-[hsl(var(--brand-green))] text-white px-8 py-3.5 text-sm font-medium hover:bg-[hsl(var(--brand-green-strong))] transition-colors disabled:opacity-50"
                >
                  {loading ? "Wird gespeichert…" : "Abonnieren"}
                </button>
              </div>
              <p className="mt-3 text-right text-xs text-neutral-500">
                <label className="inline-flex items-start gap-2 cursor-pointer">
                  <input type="checkbox" checked={consent} onChange={(e) => setConsent(e.target.checked)} data-testid="newsletter-consent" className="mt-0.5 accent-[hsl(var(--brand-green-strong))]" />
                  <span>
                    Mit der Anmeldung akzeptieren Sie unsere{" "}
                    <Link to="/datenschutz" className="text-[hsl(var(--brand-green-strong))] underline hover:no-underline">Datenschutzhinweise</Link>.
                  </span>
                </label>
              </p>
            </>
          )}
        </form>
      </div>
    </div>
  );
}
